import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import { twMerge } from "tailwind-merge";

import { ErrorView } from "@/components/error-view";
import { useLang } from "@/hooks/use-lang";
import { useUpdateQuery } from "@/hooks/use-update-query";

import { Badge } from "ui/widget/badge";
import { Select } from "ui/widget/select";
import { Skeletons } from "ui/widget/skeletons";
import { ChartSkeletons } from "ui/widget/skeletons/Skeletons";

import {
  getTop20CostEstimationChartData,
  getTop20CostEstimationChartInfo,
} from "../../../data/top20-cost-estimation-chart";
import { useInvestorTop20 } from "../../../services/get-investor-top20";
import { useInvestorTop20Graph } from "../../../services/get-investor-top20-graph";

const ULineChart = dynamic(
  () => import("ui/analysis/u-line-chart").then((mod) => mod.ULineChart),
  { ssr: false, loading: () => <ChartSkeletons /> }
);

export const Top20CostEstimationGraph = ({
  investor,
}: {
  investor: string;
}) => {
  const { t } = useLang();
  const searchParams = useSearchParams();
  const { data, isLoading, isError } = useInvestorTop20({ investor });
  const selectedSymbolIndex = +(searchParams.get("symbol-index") ?? "0");
  const selectedSymbol = data?.[selectedSymbolIndex] ?? null;

  return (
    <div className="flex flex-col items-center">
      {isLoading || isError ? (
        <Skeletons />
      ) : (
        <SymbolPanel selectedSymbol={selectedSymbol} symbols={data} />
      )}
      <p className="pt-5 text-sm font-medium">{`${selectedSymbol ?? ""} ${
        t.my13F.investor.costEstimation
      }`}</p>
      <EstimationGraph investor={investor} selectedSymbol={selectedSymbol} />
    </div>
  );
};

const SymbolPanel = ({
  selectedSymbol,
  symbols,
}: {
  selectedSymbol: string | null;
  symbols: string[];
}) => {
  const { updateQuery } = useUpdateQuery();
  const handleOnChanged = (value: string) => {
    const selectedIndex = symbols.findIndex((symbol) => symbol === value);
    updateQuery({
      name: "symbol-index",
      value: selectedIndex.toString(),
    });
  };

  return (
    <>
      {/* show on pad, screen */}
      <div className="hidden flex-wrap gap-x-4 gap-y-3 rounded-lg border border-[#CFD6DC] px-8 py-4 text-neutral-500 @md:flex">
        {symbols.map((item) => (
          <Badge
            className={twMerge(
              "h-6 min-w-[65px] cursor-pointer rounded-xl bg-[#f5f5f5] hover:bg-[#58C1D7] hover:text-white hover:shadow-md",
              selectedSymbol === item &&
                "cursor-default bg-[#58C1D7] text-white shadow-md"
            )}
            key={item}
            onClick={() => handleOnChanged(item)}
            role="button"
            aria-label="view cost estimation"
          >
            {item}
          </Badge>
        ))}
      </div>
      {/* show on mobile */}
      <Select
        data={symbols}
        value={selectedSymbol}
        onValueChange={handleOnChanged}
        className="flex h-[35px] w-[125px] self-start @md:hidden"
        MenuProps={{ style: { maxHeight: 300 } }}
        id="top20-cost-estimation-symbol-select"
      />
    </>
  );
};

const EstimationGraph = ({
  investor,
  selectedSymbol,
}: {
  investor: string;
  selectedSymbol: string | null;
}) => {
  const { t, lang } = useLang();
  const { data, isLoading, isError } = useInvestorTop20Graph({
    investor,
    symbol: selectedSymbol ?? "",
  });

  if (isLoading) return <ChartSkeletons className="mt-4" />;
  if (isError)
    return (
      <ErrorView className="mt-4 h-[250px]">{t.error.serverError}</ErrorView>
    );

  const chartData = getTop20CostEstimationChartData({ data });
  return (
    <figure aria-label="cost estimation chart" className="w-full">
      <ULineChart
        data={chartData}
        dataInfo={getTop20CostEstimationChartInfo({ t })}
        lang={lang}
        height={250}
        showLegend="static"
        showTooltip
        dateLabel={t.my13F.investor.date}
      />
    </figure>
  );
};
